import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { CheckCheck } from 'lucide-react';
import { queryKeys } from '@/shared/api/queryKeys';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { previewMessage } from '../api/draftsApi';
import { tint } from './styles';

const PREVIEW_DELAY_MS = 500;

/** WhatsApp's own markup: *bold* between asterisks. */
function Line({ text }: { text: string }) {
  return text.split(/(\*[^*\n]+\*)/g).map((part, i) =>
    part.startsWith('*') && part.endsWith('*') && part.length > 2 ? (
      <Box component="strong" key={`b-${i}`} sx={{ fontWeight: 700 }}>{part.slice(1, -1)}</Box>
    ) : (
      part
    ),
  );
}

/** The message lines, with the disclaimer set apart in a quieter tone as WhatsApp users will see it. */
function Body({ text, disclaimer }: { text: string; disclaimer: string }) {
  const at = disclaimer ? text.lastIndexOf(disclaimer) : -1;
  const message = at >= 0 ? text.slice(0, at).trimEnd() : text;
  return (
    <Typography variant="body2" component="div" sx={{ lineHeight: 1.55 }}>
      {message.split('\n').map((line, i) => <div key={`l-${i}`}>{line ? <Line text={line} /> : ' '}</div>)}
      {at >= 0 ? <Box sx={{ mt: 1, color: 'text.secondary', fontSize: '0.75rem' }}>{disclaimer}</Box> : null}
    </Typography>
  );
}

type Props = { text: string; disclaimer: string; time?: string | undefined };

/** The message as a recipient will see it on WhatsApp, formatted by the server code that sends it, so it updates
 * as the analyst edits. */
export function WhatsAppPreview({ text, disclaimer, time = 'now' }: Props) {
  const settled = useDebouncedValue(text.trim(), PREVIEW_DELAY_MS);
  const { data } = useQuery({
    queryKey: queryKeys.drafts.preview(settled),
    queryFn: () => previewMessage(settled),
    enabled: settled !== '',
    staleTime: Infinity,
  });

  return (
    <Stack component="section" aria-labelledby="wa-preview" sx={{ gap: 1 }}>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography id="wa-preview" variant="caption" component="h3" sx={{ fontWeight: 600 }}>Message preview</Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>How it reads on WhatsApp · disclaimer added automatically</Typography>
      </Stack>
      <Box sx={backdropSx}>
        <Box sx={bubbleSx} aria-label="WhatsApp preview">
          {data ? <Body text={data.text} disclaimer={disclaimer} /> : ['100%', '90%', '60%'].map((w) => <Skeleton key={w} variant="text" width={w} />)}
          <Stack direction="row" sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.5, color: 'text.secondary' }}>
            <Typography variant="caption" sx={{ fontSize: '0.625rem' }}>{time}</Typography>
            <Box component="span" sx={{ display: 'flex', color: 'info.main' }}><CheckCheck size={13} aria-hidden /></Box>
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}

const backdropSx = {
  p: { xs: 1.5, md: 2 },
  borderRadius: 1.5,
  bgcolor: tint('warning-main', 9),
} as const;

const bubbleSx = {
  maxWidth: 440,
  px: 1.5,
  pt: 1,
  pb: 0.5,
  borderRadius: 1.5,
  borderTopLeftRadius: 0.25,
  bgcolor: 'color-mix(in srgb, var(--mui-palette-success-main) 22%, var(--mui-palette-background-paper))',
  boxShadow: '0 1px 1px rgba(26, 26, 25, 0.08)',
  '@keyframes bubbleIn': { from: { opacity: 0, transform: 'scale(0.98) translateY(4px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'bubbleIn 260ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;
